import { AssetIcon, Button, ConnectWalletButton, Link, Skeleton } from "@/components";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ROUTES } from "@/config";
import { usePersistentQueryValue, useProtocolStore } from "@/hooks";
import { formatTokenAmountForDisplay } from "@/utils";
import { ClickAwayListener } from "@mui/material";
import { IoMenuSharp } from "react-icons/io5";
import { FaMoon, FaSun } from "react-icons/fa";

type NavItem =
    | {
          label: string;
          path: `/${string}`;
          external?: false;
      }
    | {
          label: string;
          path: `https://${string}`;
          external: true;
      };

const NAV_ITEMS: NavItem[] = [
    { label: "Earn", path: ROUTES.HOME },
    { label: "Lock", path: ROUTES.LOCK },
    { label: "Mint", path: ROUTES.MINT },
    { label: "Portfolio", path: ROUTES.PORTFOLIO },
    { label: "Points", path: ROUTES.POINTS },
    { label: "Stats", path: "https://stats.infinifi.xyz", external: true },
    // { label: "Vote", path: ROUTES.VOTE },
];

const NAVBAR_CLASSES =
    "px-4 sm:px-4 md:px-6 flex items-center max-h-[40px] min-h-[40px] rounded-[7px] bg-[var(--card-bg)] border-primary/20 drop-shadow-[0,6px,15.2,0,rgba(0,0,0,0.04)] fixed top-4 max-w-[1126px] border-[1px] z-50";

const NAVBAR_LINK_BASE_CLASSES =
    "mx-4 flex flex-col justify-center text-[15px] leading-[15px] font-normal transition-all duration-300 relative";
const NAVBAR_LINK_ACTIVE_CLASSES =
    "text-primary bg-[radial-gradient(circle_at_top,var(--tw-gradient-stops))_from_top_/_var(--tw-gradient-stops)]";
const NAVBAR_LINK_INACTIVE_CLASSES = "text-dark hover:text-dark";

const NavbarLink = memo(function NavbarLink({
    navItem,
    isActive,
    className = "",
    onClick,
}: {
    navItem: NavItem;
    isActive: boolean;
    className?: string;
    onClick?: () => void;
}) {
    const { label, path } = navItem;
    const external = navItem.external ?? false;

    const linkClassName = `${NAVBAR_LINK_BASE_CLASSES} ${
        isActive ? NAVBAR_LINK_ACTIVE_CLASSES : NAVBAR_LINK_INACTIVE_CLASSES
    } ${className}`;

    if (external) {
        return (
            <Link href={path} target="_blank" rel="noopener noreferrer" className={linkClassName} onClick={onClick}>
                {label}
            </Link>
        );
    }

    return (
        <Link href={path} className={linkClassName} target="_self" rel="noopener noreferrer" onClick={onClick}>
            {label}
        </Link>
    );
});

type NavbarTvlProps = {
    isLoading: boolean;
    tvl: string;
};
const NavbarTvl = memo(function NavbarTvl({ isLoading, tvl }: NavbarTvlProps) {
    return (
        <div>
            {isLoading ? (
                <Skeleton variant="text" width={100} height={28} />
            ) : (
                <span className="text-primary text-[12px] md:text-[15px] whitespace-nowrap">TVL: ${tvl}</span>
            )}
        </div>
    );
});

interface NavbarProps {
    themeMode: "light" | "dark";
    activeTheme: string;
    isThemeToggleDisabled: boolean;
    onToggleTheme: () => void;
}

export const Navbar = memo(function Navbar({
    themeMode,
    activeTheme,
    isThemeToggleDisabled,
    onToggleTheme,
}: NavbarProps) {
    const { pathname } = useLocation();
    const { stats, isLoading } = useProtocolStore();
    const tvl = formatTokenAmountForDisplay(stats.asset.totalTVLAssetNormalized.toFixed(0));
    usePersistentQueryValue("ref");
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const toggleMobileMenu = useCallback(() => {
        setIsMobileMenuOpen((prev) => !prev);
    }, []);

    const closeMobileMenu = useCallback(() => {
        setIsMobileMenuOpen(false);
    }, []);

    useEffect(() => {
        closeMobileMenu();
    }, [closeMobileMenu, pathname]);

    const useDarkLogo = useMemo(() => {
        if (activeTheme === "dark") return true;
        if (activeTheme === "light") return false;
        return true;
    }, [activeTheme]);

    const navLinks = useMemo(
        () => (
            <>
                {NAV_ITEMS.map((item) => {
                    const { label, path } = item;
                    return <NavbarLink key={label} navItem={item} isActive={pathname === path} />;
                })}
            </>
        ),
        [pathname],
    );

    const mobileNavLinks = useMemo(
        () =>
            NAV_ITEMS.map((item) => {
                const { label, path } = item;
                return (
                    <NavbarLink
                        key={`mobile-${label}`}
                        navItem={item}
                        isActive={pathname === path}
                        onClick={closeMobileMenu}
                        className="mx-0 px-4 py-3 hover:bg-primary/5"
                    />
                );
            }),
        [closeMobileMenu, pathname],
    );

    const themeToggleIcon = useMemo(
        () =>
            themeMode === "dark" ? (
                <FaSun size={12} color="rgb(var(--color-secondary))" />
            ) : (
                <FaMoon size={12} color="rgb(var(--color-primary))" />
            ),
        [themeMode],
    );

    const themeToggleAriaLabel = isThemeToggleDisabled
        ? "Theme toggle disabled while the current page theme override is active"
        : `Switch to ${themeMode === "dark" ? "light" : "dark"} theme`;

    return (
        <>
            <nav className={NAVBAR_CLASSES} style={{ width: "100%" }}>
                {/* Desktop layout: single row */}
                <div className="flex flex-row items-center justify-between w-full">
                    <div className="col-span-1 ">
                        <Link href="/">
                            {useDarkLogo ? (
                                <AssetIcon
                                    name="INFINIFI_DARK"
                                    className="h-[30px] sm:h-[30px] min-w-[100px] sm:inline-flex hidden"
                                />
                            ) : (
                                <AssetIcon
                                    name="INFINIFI"
                                    className="h-[30px] sm:h-[30px] min-w-[100px] sm:inline-flex hidden "
                                />
                            )}
                            <AssetIcon name="INFINIFI_X" className="h-[24px] min-w-[24px] mr-2 inline-flex sm:hidden" />
                        </Link>
                    </div>

                    <div className="flex-1 items-center justify-center col-span-1 gap-0 hidden lg:flex">{navLinks}</div>

                    <div className="col-span-1 inline-flex items-center justify-end gap-4 lg:gap-2">
                        <NavbarTvl isLoading={isLoading} tvl={tvl} />
                        <Button
                            variant="text"
                            size="sm"
                            className="hidden lg:inline-flex items-center justify-center "
                            disabled={isThemeToggleDisabled}
                            onClick={onToggleTheme}
                            aria-label={themeToggleAriaLabel}
                        >
                            {themeToggleIcon}
                        </Button>
                        <ConnectWalletButton />
                    </div>

                    <div className="inline-flex gap-0">
                        <Button
                            variant="text"
                            size="sm"
                            className="lg:hidden inline-flex items-center justify-center px-2"
                            disabled={isThemeToggleDisabled}
                            onClick={onToggleTheme}
                            aria-label={themeToggleAriaLabel}
                        >
                            {themeToggleIcon}
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="aspect-square text-[18px] text-dark xs:inline-flex lg:hidden px-2"
                            onClick={toggleMobileMenu}
                            aria-expanded={isMobileMenuOpen}
                            aria-controls="navbar-mobile-links"
                            aria-label="Toggle navigation menu"
                        >
                            <IoMenuSharp />
                        </Button>
                    </div>
                </div>

                {isMobileMenuOpen && (
                    <ClickAwayListener onClickAway={closeMobileMenu}>
                        <div
                            id="navbar-mobile-links"
                            className="absolute right-2 top-[calc(100%+8px)] z-50 w-[300px] rounded-[7px] border-[1px] border-primary/20 bg-card p-0 shadow-[0_10px_24px_rgba(0,0,0,0.08)] backdrop-blur-sm lg:hidden"
                        >
                            <div className="flex flex-col gap-0">{mobileNavLinks}</div>
                        </div>
                    </ClickAwayListener>
                )}
            </nav>
            <div className="mb-[40px]" />
        </>
    );
});

NavbarLink.displayName = "NavbarLink";

NavbarTvl.displayName = "NavbarTvl";
